import { useEffect, useState } from 'react';
import { Activity, AlertCircle, Loader2, RefreshCw, TrendingUp, Zap } from 'lucide-react';
import { api } from '../lib/api.js';
import { Badge, GlassButton, GlassCard, GradientText, PageHeader, StatCard } from '../ui/index.js';

interface MonitoringSnapshot {
  requestsPerSec: number;
  errorRate: number;
  costPerSec: number;
  latencyP50Ms: number;
  latencyP95Ms: number;
  activeKeys: number;
  totalRequests: number;
  byProvider: Record<string, { requests: number; errors: number; costUsd: number }>;
  recentRequests: Array<{ ts: number; model: string; status: number; costUsd: number; latencyMs: number; orgId: string }>;
}

export function PlatformMonitoringPage() {
  const [snapshot, setSnapshot] = useState<MonitoringSnapshot | null>(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    try {
      const result = await api.get<MonitoringSnapshot>('/api/v1/platform/monitoring');
      setSnapshot(result);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    const interval = setInterval(() => void load(), 5000);
    return () => clearInterval(interval);
  }, []);

  if (loading && !snapshot) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-indigo-400" /></div>;
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={<GradientText>Monitoring</GradientText>}
        subtitle="Real-time router metrics. Auto-refreshes every 5 seconds."
        action={<GlassButton variant="secondary" icon={<RefreshCw className="h-4 w-4" />} onClick={() => void load()}>Refresh</GlassButton>}
      />

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Requests / sec" value={snapshot?.requestsPerSec.toFixed(1) ?? '0'} icon={<Zap className="h-4 w-4" />} change="live" changeType="positive" />
        <StatCard label="Error rate" value={`${(snapshot?.errorRate ?? 0).toFixed(1)}%`} icon={<AlertCircle className="h-4 w-4" />} change={snapshot && snapshot.errorRate > 5 ? 'high' : 'ok'} changeType={snapshot && snapshot.errorRate > 5 ? 'negative' : 'positive'} />
        <StatCard label="Cost / sec" value={`$${(snapshot?.costPerSec ?? 0).toFixed(4)}`} icon={<TrendingUp className="h-4 w-4" />} change="live" changeType="neutral" />
        <StatCard label="Active keys" value={String(snapshot?.activeKeys ?? 0)} icon={<Activity className="h-4 w-4" />} changeType="neutral" />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <GlassCard className="space-y-2">
          <h3 className="text-sm font-semibold text-white">Latency</h3>
          <div className="flex items-center gap-6">
            <div>
              <p className="text-xs text-white/40">p50</p>
              <p className="text-2xl text-white/85">{(snapshot?.latencyP50Ms ?? 0).toFixed(0)}ms</p>
            </div>
            <div>
              <p className="text-xs text-white/40">p95</p>
              <p className="text-2xl text-white/85">{(snapshot?.latencyP95Ms ?? 0).toFixed(0)}ms</p>
            </div>
          </div>
        </GlassCard>

        <GlassCard className="space-y-2">
          <h3 className="text-sm font-semibold text-white">Total Requests (60s window)</h3>
          <p className="text-2xl text-white/85">{snapshot?.totalRequests ?? 0}</p>
        </GlassCard>
      </div>

      {snapshot && Object.keys(snapshot.byProvider).length > 0 && (
        <GlassCard className="p-0">
          <div className="p-4">
            <h3 className="text-sm font-semibold text-white">By Provider</h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-xs uppercase tracking-wider text-white/40">
                <th className="pb-2 pr-4">Provider</th>
                <th className="pb-2 pr-4 text-right">Requests</th>
                <th className="pb-2 pr-4 text-right">Errors</th>
                <th className="pb-2 pr-4 text-right">Cost</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(snapshot.byProvider).map(([provider, stats]) => (
                <tr key={provider} className="border-b border-white/5">
                  <td className="py-2 pr-4 text-white/85">{provider}</td>
                  <td className="py-2 pr-4 text-right text-white/60">{stats.requests}</td>
                  <td className="py-2 pr-4 text-right">
                    <Badge variant={stats.errors > 0 ? 'error' : 'success'}>{stats.errors}</Badge>
                  </td>
                  <td className="py-2 pr-4 text-right text-white/60">${stats.costUsd.toFixed(4)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </GlassCard>
      )}

      {snapshot && snapshot.recentRequests.length > 0 && (
        <GlassCard className="p-0">
          <div className="p-4">
            <h3 className="text-sm font-semibold text-white">Recent Requests</h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-xs uppercase tracking-wider text-white/40">
                <th className="pb-2 pr-4">Time</th>
                <th className="pb-2 pr-4">Model</th>
                <th className="pb-2 pr-4">Status</th>
                <th className="pb-2 pr-4 text-right">Latency</th>
                <th className="pb-2 pr-4 text-right">Cost</th>
                <th className="pb-2 pr-4">Org</th>
              </tr>
            </thead>
            <tbody>
              {snapshot.recentRequests.map((req, i) => (
                <tr key={i} className="border-b border-white/5">
                  <td className="py-2 pr-4 text-white/40 text-xs">{new Date(req.ts).toLocaleTimeString()}</td>
                  <td className="py-2 pr-4 text-white/85">{req.model}</td>
                  <td className="py-2 pr-4"><Badge variant={req.status < 400 ? 'success' : 'error'}>{req.status}</Badge></td>
                  <td className="py-2 pr-4 text-right text-white/60">{req.latencyMs}ms</td>
                  <td className="py-2 pr-4 text-right text-white/60">${req.costUsd.toFixed(4)}</td>
                  <td className="py-2 pr-4 text-white/40 font-mono text-xs">{req.orgId.slice(0, 8)}...</td>
                </tr>
              ))}
            </tbody>
          </table>
        </GlassCard>
      )}
    </div>
  );
}
